import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { RmsUtilsService } from './rms-utils.service';
import { FinancialUtilsService } from './financial-utils.service';
import { CONFIG } from '../../config';
import { RmsColors } from '../../shared/constants/rms-colors';

/**
 * Injectable shared service to handle charts functionality
 */
@Injectable()
export class ChartService {

  public chartConfig: any;
  public dateFormat = 'MMM dd, yyyy';
  public dateRangeFormat = 'MMM dd, yyyy';
  public dataDateFormat = 'yyyy-MM-dd';
  public axisDateFormat = 'MM/dd/yy';
  public datePickerFormat = CONFIG.rangeDateFormat;
  constructor() {
    /**
     * Initial chart configuration
     * This can be assigned in the component class and updated/extended with Object.assign
     * @type {}
     */
    this.chartConfig = {
      color: [RmsColors.RMS_TEAL_COLOR, '#ACC1CE', '#C1B1C6', RmsColors.RMS_PRIMARY_COLOR],
      legend: {
        x: 'left',
        // Explicit, because echarts 6 defaults an unanchored legend to the bottom of the chart where
        // it lands on the dataZoom slider. Every chart built against this config assumes the top.
        top: 0
      },
      toolbox: {
        show: true,
        feature: {
          dataZoom: {
            yAxisIndex: 'none',
            title: {
              zoom: 'Drag to Zoom',
              back: 'Back'
            }
          }
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          snap: false
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: 50,
        containLabel: true
      },
      dataZoom: [
        {
          type: 'slider',
          show: true,
          startValue: `${DateTime.now().year}-01-01`,
          end: 100,
          labelFormatter: (value) => (DateTime.fromMillis(value).toFormat('MMM dd, yyyy'))
        }
      ],
      xAxis: [
        {
          type: 'time',
          boundaryGap: '0%',
          axisTick: {
            alignWithLabel: true
          },
          axisLine: {
            onZero: true,
            lineStyle: {
              type: 'dashed'
            }
          },
          axisLabel: {
            formatter: (value) => (DateTime.fromMillis(value).toFormat('MMM dd, yyyy'))
          },
          axisPointer: {
            label: {
              show: false
            }
          }
        }
      ],
      yAxis: {
        type: 'value'
      }
    };
  }

  /**
   * Creates the chart tooltip
   * @param {Array<any>} params: list of chart line data
   * @returns {string}
   */
  public generateTooltip (params: Array<any>): string {
    params.forEach(item => item.sortingKey = item.value[1]);
    params = RmsUtilsService.sortByKey(params, 'sortingKey');
    let tt = `<table><tr><td colspan="4">${DateTime.fromMillis(params[0].axisValue).toFormat('MMM dd, yyyy')}</td></tr>`;
    params.forEach(item => {
      const seriesName = RmsUtilsService.escapeHtml(item.seriesName);
      // item.marker is an ECharts-generated color swatch, not user-controlled data, so it does not need escaping.
      tt += `<tr>
              <td>${item.marker}</td>
              <td>${seriesName}</td>
              <td style="padding-left: 25px" align="right">${item.value[1] ? item.value[1].toFixed(2) : '-'}%</td>
              <td style="padding-left: 25px" align="right">
                ${item.value[2] ? '$' + FinancialUtilsService.convertMoney(item.value[2], 2) : 0}
              </td>
            </tr>`;
    });
    return tt += '</table>';
  }

}
